import {useEffect, useRef, useState} from 'react'

import {isDragDropColumnId} from '../components/react_table/column-ids'
import {focusCell, focusOmnibar, type TableFocus} from '../components/react_table/navigation'
import type {MemexItemModel} from '../models/memex-item-model'
import type {SetFocusStateAction} from '../navigation/context'
import {rowIsGrouped} from './tanstack-table/row-api'
import type {TableInstance} from './tanstack-table/types'
import {useViews} from './use-views'

type SetTableFocusAction = SetFocusStateAction<TableFocus>

function getFocusFirstCellAction(table: TableInstance<MemexItemModel>) {
  const firstRow = table.flatRows.find(row => !rowIsGrouped(row))

  const firstVisibleColumn = table.visibleColumns.find(column => !isDragDropColumnId(column.id))

  if (firstVisibleColumn && firstRow) {
    return focusCell(firstRow.id, firstVisibleColumn.id)
  }

  return null
}

export function useReinitializeFocusStateEffect({
  currentViewNumber,
  setInitialFocalPoint,
  table,
}: {
  currentViewNumber?: number
  setInitialFocalPoint: (action: SetTableFocusAction | null) => void
  table: TableInstance<MemexItemModel>
}) {
  const previousCurrentViewNumber = useRef(currentViewNumber)

  /**
   * This effect should only run
   * - When the view number changes
   * - when the memex is not new
   */
  useEffect(() => {
    const isViewNumberUnchanged = previousCurrentViewNumber.current === currentViewNumber

    // When we have a view that's unchanged, skip running this
    if (isViewNumberUnchanged) return

    setInitialFocalPoint(getFocusFirstCellAction(table))
  }, [currentViewNumber, setInitialFocalPoint, table])

  useEffect(() => {
    previousCurrentViewNumber.current = currentViewNumber
  })
}

/**
 * A hook that tracks the initial focusable cell
 * for a given table, in state - re-evaluating
 * whenever the currentView.number changes
 */
export const useInitialTableFocusAction = (table: TableInstance<MemexItemModel>) => {
  const {currentView} = useViews()

  const [initialFocalPoint, setInitialFocalPoint] = useState<SetTableFocusAction | null>(() => {
    return getFocusFirstCellAction(table) ?? focusOmnibar()
  })

  useReinitializeFocusStateEffect({
    currentViewNumber: currentView?.number,
    setInitialFocalPoint,
    table,
  })

  return initialFocalPoint
}
